// src/components/LanguageSwitcher.jsx
import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧', native: 'English' },
  { code: 'kn', label: 'Kannada', flag: '🇮🇳', native: 'ಕನ್ನಡ' },
  { code: 'hi', label: 'Hindi',   flag: '🇮🇳', native: 'हिन्दी' },
]

export default function LanguageSwitcher({ variant = 'topbar' }) {
  const { i18n } = useTranslation()
  const [open, setOpen]   = useState(false)
  const ref               = useRef(null)
  const current           = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const change = (code) => {
    i18n.changeLanguage(code)
    setOpen(false)
  }

  // ── Topbar variant (dark background) ──────────────────────────
  if (variant === 'topbar') {
    return (
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 10px', borderRadius: 6,
            border: '1px solid rgba(255,255,255,.15)',
            background: 'transparent', cursor: 'pointer',
            color: '#a8c4f0', fontSize: 12, fontWeight: 500,
            fontFamily: 'inherit', transition: 'all .15s',
          }}
          aria-label="Change language"
          aria-expanded={open}
        >
          <span>{current.flag}</span>
          <span>{current.native}</span>
          <i className="ti ti-chevron-down" style={{ fontSize: 11, opacity: .7 }} aria-hidden="true" />
        </button>

        {open && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: 'var(--shadow-md)',
            minWidth: 150, zIndex: 999, overflow: 'hidden',
          }}>
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                onClick={() => change(l.code)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                  padding: '9px 13px', border: 'none', cursor: 'pointer',
                  background: l.code === i18n.language ? 'var(--surface-alt)' : 'var(--surface)',
                  fontSize: 13, fontFamily: 'inherit', color: 'var(--gray-900)',
                  fontWeight: l.code === i18n.language ? 600 : 400,
                  transition: 'background .1s', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 16 }}>{l.flag}</span>
                <span>{l.native}</span>
                {l.code === i18n.language && (
                  <i className="ti ti-check" style={{ marginLeft: 'auto', color: 'var(--green-600)', fontSize: 14 }} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── Login page variant (light background) ─────────────────────
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 20,
          border: '1px solid var(--border)',
          background: 'var(--surface-alt)', cursor: 'pointer',
          color: 'var(--gray-700)', fontSize: 13, fontWeight: 500,
          fontFamily: 'inherit', transition: 'all .15s',
        }}
        aria-label="Change language"
        aria-expanded={open}
      >
        <span style={{ fontSize: 16 }}>{current.flag}</span>
        <span>{current.native}</span>
        <i className="ti ti-chevron-down" style={{ fontSize: 11 }} aria-hidden="true" />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: '50%',
          transform: 'translateX(-50%)', marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-md)',
          minWidth: 160, zIndex: 999, overflow: 'hidden',
        }}>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              onClick={() => change(l.code)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 13px', border: 'none', cursor: 'pointer',
                background: l.code === i18n.language ? 'var(--surface-alt)' : 'var(--surface)',
                fontSize: 13, fontFamily: 'inherit', color: 'var(--gray-900)',
                fontWeight: l.code === i18n.language ? 600 : 400,
                transition: 'background .1s', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 16 }}>{l.flag}</span>
              <div>
                <div>{l.native}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{l.label}</div>
              </div>
              {l.code === i18n.language && (
                <i className="ti ti-check" style={{ marginLeft: 'auto', color: 'var(--green-600)', fontSize: 14 }} aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
